/**
 * Motion effects — native parallax and in-view animations.
 * Honors prefers-reduced-motion and supports Shopify theme editor reloads.
 *
 * Wrap (clips overflow — use for images):
 *
 *   <motion-parallax-scroll delta="120" direction="down">
 *     {{ image | image_url: width: 1400 | image_tag }}
 *   </motion-parallax-scroll>
 *
 * Mark an existing element (no clip — use for type):
 *
 *   <h2 data-parallax="80" data-parallax-direction="up">…</h2>
 *
 * Animate when entering the viewport:
 *
 *   <div
 *     data-motion="pop-in"
 *     data-motion-delay="0.1"
 *     data-motion-duration="0.75"
 *     data-motion-amount="0.33"
 *   >
 *     …
 *   </div>
 *
 * Add data-motion-repeat to reset after leaving and replay on re-entry.
 *
 * Attributes (on the custom element, or as data-parallax-*):
 *   delta / data-parallax   total travel in px          default 240
 *   direction               down | up | left | right    default down
 *   y1 y2 x1 x2             exact endpoints (px or %)   from delta + direction
 *   mobile-delta, mobile-y1 …  overrides below 749px
 *   no-clip                 don't clip the wrapper
 */

const STYLE_ID = 'motion-parallax-scroll-css';
const MOBILE_MAX_W = 749;
const DEFAULT_DELTA = 240;
const DEFAULT_FADE_DURATION = 0.75;
const DEFAULT_FADE_AMOUNT = 0.33;
const LAYER_ATTR = 'data-parallax-layer';

/** @type {WeakMap<HTMLElement, ParallaxController>} */
const controllers = new WeakMap();

/** @type {WeakMap<HTMLElement, ViewAnimationController>} */
const viewAnimationControllers = new WeakMap();

/** @type {Set<ParallaxController>} */
const fallbackActive = new Set();

let fallbackRaf = 0;
let fallbackListening = false;

/**
 * @typedef {object} ParallaxEndpoints
 * @property {number} x1
 * @property {number} x2
 * @property {number} y1
 * @property {number} y2
 */

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
motion-parallax-scroll {
  display: block;
  overflow: clip;
}
motion-parallax-scroll[no-clip] {
  overflow: visible;
}
@keyframes motion-parallax-scroll {
  0% { translate: var(--parallax-x1, 0px) var(--parallax-y1, 0px); }
  100% { translate: var(--parallax-x2, 0px) var(--parallax-y2, 0px); }
}
@media (prefers-reduced-motion: no-preference) {
  @supports (animation-timeline: view()) {
    motion-parallax-scroll,
    [data-parallax]:not(motion-parallax-scroll) {
      view-timeline-name: --parallax-scroll;
      view-timeline-axis: block;
    }
    .motion-parallax__layer:not([data-parallax-js]) {
      animation-name: motion-parallax-scroll;
      animation-duration: 1ms;
      animation-timing-function: linear;
      animation-fill-mode: both;
      animation-timeline: --parallax-scroll;
      animation-range: cover;
    }
  }
}
@supports not (animation-timeline: view()) {
  .motion-parallax__layer {
    will-change: translate;
  }
}
`;
  document.head.appendChild(style);
}

function hasViewTimeline() {
  try {
    if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
      if (CSS.supports('animation-timeline', 'view()')) return true;
      if (CSS.supports('animation-timeline: view()')) return true;
    }
    if (typeof Reflect.get(globalThis, 'ViewTimeline') === 'function') return true;
  } catch {
    return false;
  }
  return false;
}

function prefersReducedMotion() {
  return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);
}

function isDesignMode() {
  return Boolean(Reflect.get(window, 'Shopify')?.designMode);
}

const USE_CSS_TIMELINE = hasViewTimeline();

/**
 * @param {HTMLElement} el
 * @param {string} name
 * @param {number} fallback
 * @returns {number}
 */
function numberDataAttribute(el, name, fallback) {
  const value = Number.parseFloat(el.getAttribute(name) || '');
  return Number.isFinite(value) ? value : fallback;
}

/**
 * @param {HTMLElement} el
 * @param {string} name
 * @returns {string | null}
 */
function readAttr(el, name) {
  const direct = el.getAttribute(name);
  if (direct != null && direct !== '') return direct;
  const data = el.getAttribute(`data-parallax-${name}`);
  if (data != null && data !== '') return data;
  return null;
}

/**
 * @param {HTMLElement} el
 * @param {string} name
 * @param {string} mobileName
 * @returns {string | null}
 */
function readResponsiveAttr(el, name, mobileName) {
  if (window.innerWidth < MOBILE_MAX_W) {
    const mobile = readAttr(el, mobileName);
    if (mobile != null) return mobile;
  }
  return readAttr(el, name);
}

/**
 * @param {string | null} raw
 * @returns {number | string | null}
 */
function parseParallaxValue(raw) {
  if (raw == null || raw === '') return null;
  const value = raw.trim();
  if (value.includes('%')) return value;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
}

/**
 * @param {number | string | null} value
 * @param {HTMLElement} el
 * @param {'x' | 'y'} axis
 * @returns {number}
 */
function resolveLength(value, el, axis) {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string' && value.includes('%')) {
    const numeric = Number.parseFloat(value);
    if (Number.isNaN(numeric)) return 0;
    const size = axis === 'x' ? el.offsetWidth : el.offsetHeight;
    return (numeric / 100) * Math.max(size, 0);
  }
  return 0;
}

/**
 * @param {string | null} direction
 * @returns {{xSign: number, ySign: number}}
 */
function signsForDirection(direction) {
  switch ((direction || 'down').trim().toLowerCase()) {
    case 'up':
      return { xSign: 0, ySign: -1 };
    case 'left':
      return { xSign: -1, ySign: 0 };
    case 'right':
      return { xSign: 1, ySign: 0 };
    default:
      return { xSign: 0, ySign: 1 };
  }
}

/**
 * @param {HTMLElement} el
 * @returns {number}
 */
function resolveDelta(el) {
  const flagged = el.getAttribute('data-parallax');
  const flaggedValue = parseParallaxValue(flagged);
  const fromFlag = typeof flaggedValue === 'number' ? flaggedValue : null;
  const parsed = parseParallaxValue(readResponsiveAttr(el, 'delta', 'mobile-delta'));
  const fromDelta = typeof parsed === 'number' ? parsed : null;
  return fromDelta ?? fromFlag ?? DEFAULT_DELTA;
}

/**
 * @param {HTMLElement} host
 * @param {HTMLElement} layer
 * @returns {ParallaxEndpoints}
 */
function resolveEndpoints(host, layer) {
  const direction = readResponsiveAttr(host, 'direction', 'mobile-direction') || 'down';
  const signs = signsForDirection(direction);
  const delta = resolveDelta(host);
  const half = delta / 2;
  const axis = (readAttr(host, 'axis') || '').trim().toLowerCase();

  let x1 = signs.xSign * -half;
  let x2 = signs.xSign * half;
  let y1 = signs.ySign * -half;
  let y2 = signs.ySign * half;

  if (axis === 'x' || axis === 'horizontal') {
    y1 = 0;
    y2 = 0;
    if (signs.xSign === 0) {
      x1 = -half;
      x2 = half;
    }
  } else if (axis === 'y' || axis === 'vertical') {
    x1 = 0;
    x2 = 0;
    if (signs.ySign === 0) {
      y1 = -half;
      y2 = half;
    }
  }

  const explicitX1 = parseParallaxValue(readResponsiveAttr(host, 'x1', 'mobile-x1'));
  const explicitX2 = parseParallaxValue(readResponsiveAttr(host, 'x2', 'mobile-x2'));
  const explicitY1 = parseParallaxValue(readResponsiveAttr(host, 'y1', 'mobile-y1'));
  const explicitY2 = parseParallaxValue(readResponsiveAttr(host, 'y2', 'mobile-y2'));

  if (explicitX1 != null) x1 = resolveLength(explicitX1, layer, 'x');
  if (explicitX2 != null) x2 = resolveLength(explicitX2, layer, 'x');
  if (explicitY1 != null) y1 = resolveLength(explicitY1, layer, 'y');
  if (explicitY2 != null) y2 = resolveLength(explicitY2, layer, 'y');

  return { x1, x2, y1, y2 };
}

/**
 * @param {HTMLElement} layer
 * @param {ParallaxEndpoints} endpoints
 */
function applyEndpointVars(layer, endpoints) {
  layer.style.setProperty('--parallax-x1', `${endpoints.x1}px`);
  layer.style.setProperty('--parallax-x2', `${endpoints.x2}px`);
  layer.style.setProperty('--parallax-y1', `${endpoints.y1}px`);
  layer.style.setProperty('--parallax-y2', `${endpoints.y2}px`);
}

/**
 * @param {HTMLElement} host
 * @returns {HTMLElement}
 */
function resolveLayer(host) {
  if (host.tagName !== 'MOTION-PARALLAX-SCROLL') return host;
  if (host.hasAttribute('no-clip')) return host;

  const marked = host.querySelector(`:scope > [${LAYER_ATTR}]`);
  if (marked instanceof HTMLElement) return marked;

  const child = host.firstElementChild;
  if (child instanceof HTMLElement && host.childElementCount === 1) {
    child.setAttribute(LAYER_ATTR, '');
    return child;
  }

  return host;
}

/**
 * @param {HTMLElement} el
 * @returns {number}
 */
function coverProgress(el) {
  const rect = el.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const total = viewportHeight + rect.height;
  if (total <= 0) return 0;
  return Math.max(0, Math.min(1, (viewportHeight - rect.top) / total));
}

function requestFallbackTick() {
  if (fallbackRaf) return;
  fallbackRaf = requestAnimationFrame(() => {
    fallbackRaf = 0;
    for (const controller of fallbackActive) controller.updateFallback();
  });
}

function onFallbackScroll() {
  requestFallbackTick();
}

function startFallbackLoop() {
  if (fallbackListening) return;
  fallbackListening = true;
  window.addEventListener('scroll', onFallbackScroll, { passive: true });
  window.addEventListener('resize', onFallbackScroll);
}

function stopFallbackLoop() {
  if (fallbackActive.size > 0) return;
  fallbackListening = false;
  window.removeEventListener('scroll', onFallbackScroll);
  window.removeEventListener('resize', onFallbackScroll);
  if (fallbackRaf) cancelAnimationFrame(fallbackRaf);
  fallbackRaf = 0;
}

class ViewAnimationController {
  /**
   * @param {HTMLElement} el
   */
  constructor(el) {
    this.el = el;
    /** @type {IntersectionObserver | null} */
    this.observer = null;
    /** @type {Animation | null} */
    this.animation = null;
  }

  start() {
    if (prefersReducedMotion()) {
      this.el.setAttribute('data-motion-played', '');
      return;
    }

    if (typeof IntersectionObserver !== 'function') {
      this.play();
      return;
    }

    const amount = isDesignMode()
      ? 0
      : Math.min(Math.max(numberDataAttribute(this.el, 'data-motion-amount', DEFAULT_FADE_AMOUNT), 0), 1);

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.play();
            if (!this.el.hasAttribute('data-motion-repeat')) this.stopObserving();
          } else if (this.el.hasAttribute('data-motion-repeat')) {
            this.reset();
          }
        }
      },
      {
        threshold: amount,
        rootMargin: isDesignMode() ? '25% 0px' : '0px',
      }
    );
    this.observer.observe(this.el);
  }

  play() {
    this.animation?.cancel();

    if (typeof this.el.animate !== 'function') {
      this.el.setAttribute('data-motion-played', '');
      return;
    }

    const duration = Math.max(
      numberDataAttribute(this.el, 'data-motion-duration', DEFAULT_FADE_DURATION),
      0
    );
    const delay = Math.max(numberDataAttribute(this.el, 'data-motion-delay', 0), 0);

    const keyframes =
      this.el.dataset.motion === 'pop-in'
        ? [
            { opacity: 0, scale: 0, transformOrigin: 'center center' },
            { opacity: 1, scale: 1, transformOrigin: 'center center' },
          ]
        : [{ opacity: 0 }, { opacity: 1 }];

    this.animation = this.el.animate(keyframes, {
      duration: duration * 1000,
      delay: delay * 1000,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      fill: 'both',
    });

    this.animation.finished
      .then(() => {
        this.el.setAttribute('data-motion-played', '');
        this.animation?.cancel();
        this.animation = null;
      })
      .catch(() => {});
  }

  reset() {
    this.animation?.cancel();
    this.animation = null;
    this.el.removeAttribute('data-motion-played');
  }

  stopObserving() {
    this.observer?.disconnect();
    this.observer = null;
  }

  stop() {
    this.stopObserving();
    this.animation?.cancel();
    this.animation = null;
  }
}

class ParallaxController {
  /**
   * @param {HTMLElement} host
   */
  constructor(host) {
    this.host = host;
    this.layer = resolveLayer(host);
    /** @type {ParallaxEndpoints | null} */
    this.endpoints = null;
    /** @type {IntersectionObserver | null} */
    this.nearObserver = null;
    this.usingFallback = false;
    this.near = false;
    /** @type {(() => void) | null} */
    this.onResize = null;
  }

  start() {
    injectStyles();
    this.layer = resolveLayer(this.host);
    this.layer.classList.add('motion-parallax__layer');
    this.syncEndpoints();

    if (prefersReducedMotion()) return;

    this.onResize = () => this.syncEndpoints();
    window.addEventListener('resize', this.onResize);

    if (USE_CSS_TIMELINE && !isDesignMode()) return;

    this.usingFallback = true;
    this.layer.setAttribute('data-parallax-js', '');
    this.observeNearViewport();
  }

  syncEndpoints() {
    this.endpoints = resolveEndpoints(this.host, this.layer);
    applyEndpointVars(this.layer, this.endpoints);
    if (this.usingFallback && this.near) this.updateFallback();
  }

  observeNearViewport() {
    if (typeof IntersectionObserver !== 'function') {
      this.near = true;
      fallbackActive.add(this);
      startFallbackLoop();
      this.updateFallback();
      return;
    }

    this.nearObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.near = true;
            fallbackActive.add(this);
            startFallbackLoop();
            this.updateFallback();
          } else {
            this.near = false;
            fallbackActive.delete(this);
            stopFallbackLoop();
          }
        }
      },
      { root: null, rootMargin: '100% 0px', threshold: 0 }
    );
    this.nearObserver.observe(this.host);
  }

  updateFallback() {
    if (!this.host.isConnected) {
      this.stop();
      return;
    }
    if (!this.endpoints) this.syncEndpoints();
    const endpoints = this.endpoints;
    if (!endpoints) return;

    const progress = coverProgress(this.host);
    const x = endpoints.x1 + (endpoints.x2 - endpoints.x1) * progress;
    const y = endpoints.y1 + (endpoints.y2 - endpoints.y1) * progress;
    this.layer.style.translate = `${x}px ${y}px`;
  }

  stop() {
    this.near = false;
    fallbackActive.delete(this);
    stopFallbackLoop();
    this.nearObserver?.disconnect();
    this.nearObserver = null;
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    this.onResize = null;
    this.layer.style.translate = '';
    this.layer.removeAttribute('data-parallax-js');
  }
}

/**
 * @param {HTMLElement} el
 */
function bind(el) {
  const existing = controllers.get(el);
  if (existing) {
    existing.stop();
    existing.start();
    return;
  }
  const controller = new ParallaxController(el);
  controllers.set(el, controller);
  controller.start();
}

/**
 * @param {HTMLElement} el
 */
function unbind(el) {
  const controller = controllers.get(el);
  if (!controller) return;
  controller.stop();
  controllers.delete(el);
}

/**
 * @param {HTMLElement} el
 */
function bindViewAnimation(el) {
  if (viewAnimationControllers.has(el)) return;
  const controller = new ViewAnimationController(el);
  viewAnimationControllers.set(el, controller);
  controller.start();
}

/**
 * @param {ParentNode} [root]
 */
function scan(root = document) {
  root.querySelectorAll('[data-parallax]').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    if (node.tagName === 'MOTION-PARALLAX-SCROLL') return;
    bind(node);
  });

  root.querySelectorAll('[data-motion="fade-in"], [data-motion="pop-in"]').forEach((node) => {
    if (node instanceof HTMLElement) bindViewAnimation(node);
  });
}

class MotionParallaxScroll extends HTMLElement {
  connectedCallback() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        bind(this);
      });
    });
  }

  disconnectedCallback() {
    unbind(this);
  }
}

if (!customElements.get('motion-parallax-scroll')) {
  customElements.define('motion-parallax-scroll', MotionParallaxScroll);
}

scan();

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (target instanceof HTMLElement) scan(target);
});
